import { useApolloClient } from '@apollo/client';
import { Button, Col, Container, Justify, Loader, Row, Theme } from '@busybusy/webapp-react-ui';
import { POSITION_UPDATE_MUTATION } from 'apollo/mutations/position-mutations';
import { MEMBER_ARCHIVED_QUERY } from 'apollo/queries/member-queries';
import classNames from 'classnames';
import { DetailsCard, HeaderDialog, Well } from 'components';
import ConditionalContainer from 'components/foundation/ConditionalContainer/ConditionalContainer';
import useJsaReportsEnabled from 'containers/jsa-reports/hooks/useJsaReportsEnabled/useJsaReportsEnabled';
import DeleteConfirmationDialog from 'containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog';
import usePhotosViewCollectionPermissionEnabled from 'containers/photos/hooks/usPhotosViewCollectionPermissionEnabled/usePhotosViewCollectionPermissionEnabled';
import { KioskPositionDetail } from 'containers/settings/positions/details/KioskPositionDetail';
import { useToastDispatch } from 'contexts/ToastContext';
import {
  useApolloPaging,
  useEmployeeManagementListNavigation,
  useMember,
  useOpenable,
  usePendingAction,
  usePositionQuery,
} from 'hooks';
import useLegacyNavigation from 'hooks/navigation/useLegacyNavigation';
import useIsInIframe from 'hooks/store/useIsInIframe';
import { castArray, isEmpty, isNil, maxBy } from 'lodash';
import compact from 'lodash/compact';
import React from 'react';
import { IMember } from 'types';
import { ClassName } from 'types/ClassName';
import ICursorable from 'types/Cursorable';
import IPosition, { IPositionBooleanKey } from 'types/Position';
import { isoTimeStampUtc } from 'utils/dateUtils';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import BreakModeDetail from './details/BreakModeDetail';
import BudgetDetail from './details/BudgetDetail';
import CertificationsDetail from './details/CertificationsDetail';
import ClockInRestrictionDetail from './details/ClockInRestrictionDetail';
import EquipmentInspectionsDetail from './details/EquipmentInspectionsDetail';
import JobCostingPromptDetail from './details/JobCostingPromptDetail';
import JsaReportsDetail from './details/JsaReportsDetail';
import PhotoVerificationSettingsDetail from './details/PhotoVerificationSettingsDetail';
import PositionDetailsActivityReportDetail from './details/PositionDetailsActivityReportDetail';
import PositionDetailsBitwiseDetail, {
  IPositionDetailsBitwiseDetailProps,
} from './details/PositionDetailsBitwiseDetail';
import PositionDetailsBooleanDetail from './details/PositionDetailsBooleanDetail';
import PositionDetailsProgressDetail from './details/PositionDetailsProgressDetail';
import PositionTitleDetails from './details/PositionTitleDetail';
import RequestTimeOffSettingsDetail from './details/RequestTimeOffSettingsDetail';
import RequireEquipmentDetail from './details/RequireEquipmentDetail';
import SafetyIncidentDetail from './details/SafetyIncidentDetail';
import SafetyInspectionsDetail from './details/SafetyInspectionsDetail';
import SafetyObservationsDetail from './details/SafetyObservationsDetail';
import SafetyTrainingsDetail from './details/SafetyTrainingsDetail';
import './position-details.scss';

export interface IPositionDetailsProps {
  className?: ClassName;
  position: IPosition;
  onUpdate: () => Promise<any>;
  onClose: () => void;
}

interface ICompanyManagementConfiguration {
  title: string;
  subtitle: string;
  positionKey: IPositionBooleanKey | IPositionBooleanKey[];
  editable: boolean;
  requirePro?: boolean;
}

const PositionDetails = (props: IPositionDetailsProps) => {
  const { className, position, onClose, onUpdate } = props;

  const client = useApolloClient();
  const cantDeleteOpenable = useOpenable();
  const deleteConfirmationDialog = useOpenable();
  const [registerDeleteAction, callDeleteAction] = usePendingAction();
  const employeeManagementNav = useEmployeeManagementListNavigation();
  const toastDispatch = useToastDispatch();
  const isPro = useFeatureFlags('PRO');
  const dailyReportIsEnabled = useFeatureFlags('DAILY_REPORTS');
  const isSafetyIncidentsEnabled = useFeatureFlags('SAFETY_INCIDENTS');
  const isSafetyInspectionsEnabled = useFeatureFlags('SAFETY_INSPECTIONS');
  const isSafetyTrainingsEnabled = useFeatureFlags('SAFETY_TRAINING');
  const isSafetyObservationsEnabled = useFeatureFlags('SAFETY_OBSERVATIONS');
  const isEquipmentInspectionsEnabled = useFeatureFlags('EQUIPMENT_INSPECTIONS');
  const { subscription } = useLegacyNavigation();
  const isAdmin = position.level === 1;
  const editable = isPro && !isAdmin;
  const { getAll } = useApolloPaging();
  const loaderOpenable = useOpenable();
  const { positions } = usePositionQuery();
  const { updateMemberPositions } = useMember();
  const isBulletinEnabled = useFeatureFlags('BULLETIN_FEATURE');
  const isEquipmentPositionSettingEnabled = useFeatureFlags('REQUIRE_EQUIPMENT_POSITION_SETTING');
  const hasAccessToBulletin = isBulletinEnabled && isPro;
  const isLocationFlaggingEnabled = useFeatureFlags('LOCATION_FLAGGING');
  const isPerDiemEnabled = useFeatureFlags('PER_DIEM');
  const isInIframe = useIsInIframe();
  const photosViewCollectionPermissionEnabled = usePhotosViewCollectionPermissionEnabled();
  const areJsaReportsEnabled = useJsaReportsEnabled();
  const isAnySafetyEnabled =
    isSafetyIncidentsEnabled ||
    isSafetyInspectionsEnabled ||
    isSafetyObservationsEnabled ||
    isSafetyTrainingsEnabled ||
    areJsaReportsEnabled;
  const isCertificationsEnabled = useFeatureFlags('CERTIFICATIONS');

  const companyManagementConfiguration: ICompanyManagementConfiguration[] = compact([
    {
      title: t('Manage Company Settings'),
      subtitle: t('View and edit organization-wide features and settings, including pay period and overtime settings.'),
      positionKey: 'manageCompanySettings',
      editable,
    },
    {
      title: t('Manage Projects'),
      subtitle: t('View, create, edit, and archive projects and subprojects; create and manage project groups.'),
      positionKey: ['manageProjects', 'manageProjectGroups'],
      editable,
    },
    {
      title: t('Manage Cost Codes'),
      subtitle: t('View, create, edit, and archive cost codes; create and manage cost code groups.'),
      positionKey: 'manageCostCodes',
      editable,
    },
    {
      title: t('Manage Equipment'),
      subtitle: t('View, create, edit, and delete equipment; view equipment reports.'),
      positionKey: ['manageEquipment', 'viewEquipmentStatus', 'viewEquipmentDetails'],
      editable,
    },
    isEquipmentInspectionsEnabled && {
      title: t('Equipment Inspections'),
      subtitle: t(
        'Allow the employee to perform routine and ad hoc inspections (All employees can complete pre- and post-operation inspections when performing clock actions).'
      ),
      positionKey: 'equipmentInspections',
      editable,
    },
    isCertificationsEnabled && {
      title: t('Certifications'),
      subtitle: t(
        'View, create, edit, and delete certifications. Optionally allow a supervisor to view certifications for his entire crew, but only be able to manage their own.'
      ),
      positionKey: 'certifications',
      editable,
    },
    {
      title: t('View Expenses'),
      subtitle: t('View labor costs for employees, projects, cost codes, and equipment; view payroll information.'),
      positionKey: ['viewMemberExpenses', 'viewProjectExpenses'],
      editable,
    },
    {
      title: t('Budgets'),
      subtitle: t(
        'View, create, edit and delete project budgets. Optionally allow the user to view, but not modify, budgets.'
      ),
      positionKey: ['manageBudgets', 'viewBudgets'],
      requirePro: true,
      editable,
    },
    {
      title: t('Manage Employee Groups'),
      subtitle: t('View, create, and manage employee groups.'),
      positionKey: 'manageEmployeeGroups',
      editable: !position.scopeByGroup && editable,
    },
    dailyReportIsEnabled && {
      title: t('Receive Field Report Prompts'),
      subtitle: t('Receive reminder notification at clock out to create a field report.'),
      positionKey: 'dailyProjectPrompts',
      requirePro: true,
      editable,
    },
    dailyReportIsEnabled &&
      position.dailyProjectPrompts && {
        title: t('Assign Reports to Subprojects'),
        subtitle: t('Receive reminder notification at clock out to create a field report for each subproject.'),
        positionKey: 'dailyProjectPromptsUseSubprojects',
        editable,
      },
    hasAccessToBulletin && {
      title: t('Manage Company Bulletin'),
      subtitle: t('Add, edit, and delete their own posts from the company bulletin.'),
      positionKey: 'manageBulletinPosts',
      requirePro: true,
      editable,
    },
  ]);

  const supervisionSettings: Array<Omit<IPositionDetailsBitwiseDetailProps, 'onUpdate' | 'editable' | 'position'>> =
    compact([
      {
        currentValue: position.manageEmployees ?? 0,
        dataKey: 'manageEmployees',
        title: t('Manage Employees for') + ':',
        dialogSubtitle: t('View, create, edit, and archive employees; set wages, position, etc.'),
      },
      {
        currentValue: position.timeEvents ?? 1,
        dataKey: 'timeEvents',
        title: t('Track Time for') + ':',
        dialogSubtitle: t('Clock in, clock out, start/end breaks, and switch; view time off.'),
        disabledMask: position.manageTimeEntries ?? 0,
      },
      {
        currentValue: position.manageTimeEntries ?? 0,
        dataKey: 'manageTimeEntries',
        title: t('Edit Time Entries for') + ':',
        dialogSubtitle: t('View, create, edit, and delete time entries; clock in at, clock out at, switch at.'),
      },
      {
        currentValue: position.manageTimeOff ?? 0,
        dataKey: 'manageTimeOff',
        title: t('Manage Time Off for') + ':',
        dialogSubtitle: t('Can add, edit, delete, and approve time off.'),
        requirePro: true,
      },
      {
        currentValue: position.manageMediaCollection ?? 0,
        dataKey: 'manageMediaCollection',
        title: t('Manage Photos Posted by') + ':',
        dialogSubtitle: t('Can edit and delete photos, notes, comments, and tags uploaded by other employees.'),
        requirePro: true,
      },
      photosViewCollectionPermissionEnabled
        ? {
            currentValue: position.viewMediaCollection ?? 1,
            dataKey: 'viewMediaCollection',
            title: t('View Photos posted by') + ':',
            dialogSubtitle: t('Can view photos and notes.'),
            requirePro: true,
            disabledMask: (position.manageMediaCollection ?? 0) | 1,
          }
        : null,
      {
        currentValue: position.memberLock ?? 0,
        dataKey: 'memberLock',
        title: t('Manage Lock Dates for') + ':',
        dialogSubtitle: t('View, set, edit, and remove employee lock dates.'),
      },
      {
        currentValue: position.viewSchedule ?? 1,
        dataKey: 'viewSchedule',
        title: t('View Schedules for') + ':',
        dialogSubtitle: t(`Can view other employee's schedule items.`),
        disabledMask: 1 | (position.manageSchedule ?? 0),
        requirePro: true,
      },
      {
        currentValue: position.manageSchedule ?? 0,
        dataKey: 'manageSchedule',
        title: t('Manage Schedule for') + ':',
        dialogSubtitle: t('View, create, edit, delete schedule items.'),
        requirePro: true,
      },
      isLocationFlaggingEnabled
        ? {
            currentValue: position.manageGpsApproval ?? 0,
            dataKey: 'manageGpsApproval',
            title: t('Authorize GPS for') + ':',
            dialogSubtitle: t('Can approve or unauthorize employee locations outside project boundaries.'),
            requirePro: true,
          }
        : null,
      isPerDiemEnabled
        ? {
            currentValue: position.managePerDiem ?? 0,
            dataKey: 'managePerDiem',
            title: t('Manage Per Diem for') + ':',
            dialogSubtitle: t('Can add, edit, delete, and approve per diem for others.'),
            requirePro: false,
          }
        : null,
    ]);

  async function onDelete() {
    loaderOpenable.open();
    const allMembers = await getAll<IMember & ICursorable>('members', {
      query: MEMBER_ARCHIVED_QUERY,
      variables: { first: 500, filter: { positionId: { equal: position.id } } },
    });

    let positionHasUsers = true;
    let archivedMembers: (IMember & ICursorable)[] = [];

    if (!isEmpty(allMembers)) {
      archivedMembers = allMembers.filter((m) => !isNil(m.archivedOn));
      if (archivedMembers.length === allMembers.length) {
        positionHasUsers = false;
      } else {
        positionHasUsers = true;
      }
    } else {
      positionHasUsers = false;
    }
    loaderOpenable.close();

    if (positionHasUsers) {
      cantDeleteOpenable.open();
    } else {
      registerDeleteAction(async () => {
        try {
          if (archivedMembers.length === allMembers.length) {
            const newPosition = maxBy(
              positions.filter((p) => p.id !== position.id),
              (p) => p.level
            );
            if (!isNil(newPosition)) {
              await updateMemberPositions(
                newPosition.id,
                archivedMembers.map((m) => m.id)
              );
            }
          }
          await client.mutate({
            mutation: POSITION_UPDATE_MUTATION,
            variables: { position: { id: position.id, deletedOn: isoTimeStampUtc() } },
          });
          onUpdate();
          onClose();
        } catch (e) {
          toastDispatch({
            type: 'OPEN',
            payload: {
              label: t('There was a problem deleting your position. Please try again later.'),
              theme: Theme.DANGER,
            },
          });
        }
      });
      deleteConfirmationDialog.open();
    }
  }

  function navigateToEmployeeManagementList() {
    employeeManagementNav();
  }

  const classes = classNames('position-details', className);

  return (
    <div className={classes}>
      <Container className="py-4 px-8">
        {isPro && !isAdmin && (
          <Row justify={Justify.FLEX_END}>
            <Button type="secondary" onClick={onDelete} className="my-4">
              {t('Delete Position')}
            </Button>
          </Row>
        )}
        {!isPro && isAdmin && !isInIframe && (
          <Well className="my-4">
            {t('Looking to customize or create positions?')}{' '}
            <span className="upgrade-to-pro-link" onClick={subscription}>
              {t('Upgrade to Pro')}.
            </span>
          </Well>
        )}

        <Row className="pt-6 pb-4" gutter={10}>
          <Col>
            <DetailsCard title={t('General')}>
              <PositionTitleDetails position={position} onUpdate={onUpdate} editable={editable} />
              <PositionDetailsBooleanDetail
                onUpdate={onUpdate}
                position={position}
                positionKey="scopeByGroup"
                title={t(`Limit Permission to Employee Groups`)}
                subtitle={t(
                  `Limit the permissions in the position to only apply to employees within the same employee group.`
                )}
                requirePro={true}
                editable={editable}
              />
            </DetailsCard>
            <DetailsCard title={t('Supervision Settings')}>
              {supervisionSettings.map((settingsProps) => (
                <PositionDetailsBitwiseDetail
                  key={settingsProps.dataKey}
                  onUpdate={onUpdate}
                  position={position}
                  {...settingsProps}
                  editable={editable}
                />
              ))}
              <PositionDetailsProgressDetail onUpdate={onUpdate} position={position} editable={editable} />

              <PositionDetailsActivityReportDetail onUpdate={onUpdate} position={position} editable={editable} />
            </DetailsCard>
          </Col>
          <Col>
            <DetailsCard title={t('Company Management')}>
              {companyManagementConfiguration.map((configProps, i) => (
                <React.Fragment key={`${configProps.positionKey ?? i}`}>
                  {castArray(configProps.positionKey).join('-').includes('manageBudgets') ? (
                    <BudgetDetail editable={configProps.editable} position={position} onUpdate={onUpdate} />
                  ) : configProps.positionKey === 'certifications' ? (
                    <CertificationsDetail editable={configProps.editable} position={position} onUpdate={onUpdate} />
                  ) : configProps.positionKey === 'equipmentInspections' ? (
                    <EquipmentInspectionsDetail
                      editable={configProps.editable}
                      position={position}
                      onUpdate={onUpdate}
                    />
                  ) : (
                    <PositionDetailsBooleanDetail
                      key={castArray(configProps.positionKey).join('-')}
                      onUpdate={onUpdate}
                      position={position}
                      {...configProps}
                    />
                  )}
                </React.Fragment>
              ))}
            </DetailsCard>
            {isAnySafetyEnabled && (
              <DetailsCard title={t('Safety')}>
                {isSafetyIncidentsEnabled && (
                  <SafetyIncidentDetail position={position} onUpdate={onUpdate} editable={editable} />
                )}
                {isSafetyInspectionsEnabled && (
                  <SafetyInspectionsDetail position={position} onUpdate={onUpdate} editable={editable} />
                )}
                {isSafetyObservationsEnabled && (
                  <SafetyObservationsDetail position={position} onUpdate={onUpdate} editable={editable} />
                )}
                {isSafetyTrainingsEnabled && (
                  <SafetyTrainingsDetail position={position} onUpdate={onUpdate} editable={editable} />
                )}
                {areJsaReportsEnabled && (
                  <JsaReportsDetail position={position} onUpdate={onUpdate} editable={editable} />
                )}
              </DetailsCard>
            )}
            <DetailsCard title={t('Other')}>
              <ClockInRestrictionDetail editable={editable} position={position} onUpdate={onUpdate} />
              <ConditionalContainer condition={isEquipmentPositionSettingEnabled}>
                <RequireEquipmentDetail editable={editable} position={position} onUpdate={onUpdate} />
              </ConditionalContainer>
              <RequestTimeOffSettingsDetail editable={editable} position={position} onUpdate={onUpdate} />
              <PhotoVerificationSettingsDetail editable={editable} position={position} onUpdate={onUpdate} />
              <JobCostingPromptDetail editable={editable} position={position} onUpdate={onUpdate} />
              <KioskPositionDetail editable={editable} position={position} onUpdate={onUpdate} />
              <BreakModeDetail editable={editable} position={position} onUpdate={onUpdate} />
            </DetailsCard>
          </Col>
        </Row>
      </Container>

      <HeaderDialog
        title={t('Action Required')}
        isOpen={cantDeleteOpenable.isOpen}
        onClose={cantDeleteOpenable.close}
        divider={false}
        className="cannot-delete-dialog"
        position="center"
      >
        <div className="cannot-delete-dialog-content">
          <div>
            {t(
              "You cannot delete a position that has employees currently assigned to it. Reassigning an employee's position can be done in the Employees Management List."
            )}
          </div>

          <Well className="my-3">
            {t('Pro tip')}:{' '}
            {t(
              'Sort by the employees by Position and select multiple employees to edit their position in a single edit.'
            )}
          </Well>

          <Button type="primary" className="mr-2" onClick={navigateToEmployeeManagementList}>
            {t('Manage Employees')}
          </Button>

          <Button type="secondary" onClick={cantDeleteOpenable.close}>
            {t('Not Now')}
          </Button>
        </div>
      </HeaderDialog>
      <DeleteConfirmationDialog
        onDelete={callDeleteAction}
        isOpen={deleteConfirmationDialog.isOpen}
        onClose={deleteConfirmationDialog.close}
        title={t('Delete position?')}
      >
        {t('This action is permanent and cannot be undone. Are you sure you want to continue?')}
      </DeleteConfirmationDialog>
      <Loader isOpen={loaderOpenable.isOpen} />
    </div>
  );
};

export default PositionDetails;
